import { Stack, Link } from '@fluentui/react';
import { useState, useEffect, useContext, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { t } from '@/strings/index';
import FeedbackForm from '@/components/Feedback/FeedbackForm';
import { UserContext } from '@/UserContext';
import { useFeedbackPolicyCheck } from '@/hooks/useFeedbackPolicyCheck';
import { logFeedbackLinkVisibility, logFeedbackLinkClicked, collectFooterFeedbackClick, collectFeedbackCompletion } from '@/telemetry/telemetryEvents';
import '@/styles/common/Footer.css';

export default function Footer() {
  const user = useContext(UserContext);
  const location = useLocation();
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [hideFooterFeedback, setHideFooterFeedback] = useState(false);
  const feedbackStartTimeRef = useRef<number | null>(null);

  // Use shared hook to check CPS policies and get initOptions
  const { isAllowed: isFeedbackAllowed, isLoading: isPolicyCheckLoading, error: policyCheckError, initOptions } = useFeedbackPolicyCheck('footer');

  // Log feedback link visibility when policy check completes
  useEffect(() => {
    if (!isPolicyCheckLoading) {
      const visibilityReason = !user?.tenantId || !user?.localAccountId
        ? 'anonymous-user'
        : policyCheckError
          ? 'error'
          : isFeedbackAllowed
            ? 'cps-allowed'
            : 'cps-blocked';

      logFeedbackLinkVisibility(isFeedbackAllowed, visibilityReason, location.pathname);
    }
  }, [isFeedbackAllowed, isPolicyCheckLoading, policyCheckError, user?.tenantId, user?.localAccountId, location.pathname]);

  // Check if inline feedback form is active (on FeedbackThanksScreen)
  useEffect(() => {
    const checkInlineFeedback = () => {
      const isInlineActive = sessionStorage.getItem('inlineFeedbackActive') === 'true';
      const wasHidden = hideFooterFeedback;

      setHideFooterFeedback(isInlineActive);

      // Log visibility change when inline feedback state changes
      if (isInlineActive !== wasHidden) {
        if (isInlineActive) {
          logFeedbackLinkVisibility(false, 'inline-active', location.pathname);
        }
      }
    };

    // Check initially
    checkInlineFeedback();

    // Listen for storage changes
    window.addEventListener('storage', checkInlineFeedback);

    // Also check periodically in case storage event doesn't fire (same tab)
    const interval = setInterval(checkInlineFeedback, 500);

    return () => {
      window.removeEventListener('storage', checkInlineFeedback);
      clearInterval(interval);
    };
  }, [hideFooterFeedback]);

  return (
    <>
      <Stack
        as="footer"
        role="contentinfo"
        horizontal
        className="app-footer"
        tokens={{ childrenGap: 8 }}
      >
        <Link href="https://support.microsoft.com/en-us/home/contact?linkquery=%20sign%20in%20help" target="_blank">
          {t('footer.help')}
        </Link>
        <Link href="https://www.microsoft.com/en-us/legal/terms-of-use" target="_blank">
          {t('footer.terms')}
        </Link>
        <Link href="https://privacy.microsoft.com/en-us/privacystatement" target="_blank">
          {t('footer.privacy')}
        </Link>
        {!hideFooterFeedback && isFeedbackAllowed && (
          <Link
            onClick={() => {
              console.log('[Footer] Give Feedback clicked, showing modal. User authenticated:', !!user);
              feedbackStartTimeRef.current = Date.now(); // Start tracking time
              logFeedbackLinkClicked(location.pathname);
              // Collect for scenario tracking
              collectFooterFeedbackClick(location.pathname, isFeedbackAllowed);
              setShowFeedbackModal(true);
            }}
            style={{ cursor: 'pointer' }}
          >
            {t('footer.feedback')}
          </Link>
        )}
      </Stack>

      {/* SDK Modal - using shared FeedbackForm component */}
      {/* For AAD auth: pass initOptions from hook. For anonymous: no initOptions needed */}
      {showFeedbackModal && (
        <FeedbackForm
          uiType="modal"
          questionText={t('feedback.modal.satisfaction')}
          feedbackSource="footer"
          authenticationType={user ? 'aad' : 'anonymous'}
          initOptions={user ? initOptions : undefined}
          currentScreen={location.pathname}
          onReady={() => {
            // FeedbackForm logs this internally now
          }}
          onSuccess={(feedbackId?: string) => {
            console.log('[Footer] Feedback submitted successfully, feedbackId:', feedbackId);
            const timeSpentMs = feedbackStartTimeRef.current ? Date.now() - feedbackStartTimeRef.current : undefined;
            // Track which screen user submitted from (for scenario DataBag journey tracking)
            collectFeedbackCompletion({
              feedbackProvided: true,
              feedbackSubmitted: true,
              feedbackCancelled: false,
              feedbackSource: 'footer',
              feedbackId,
              currentScreen: location.pathname,
              timeSpentMs,
            });
            setShowFeedbackModal(false);
          }}
          onError={(error: string) => {
            console.error('[Footer] Feedback submission failed:', error);
            const timeSpentMs = feedbackStartTimeRef.current ? Date.now() - feedbackStartTimeRef.current : undefined;
            // Track error (for scenario DataBag)
            collectFeedbackCompletion({
              feedbackProvided: false,
              feedbackSubmitted: false,
              feedbackCancelled: false,
              feedbackSource: 'footer',
              errorMessage: error,
              currentScreen: location.pathname,
              timeSpentMs,
            });
            setShowFeedbackModal(false);
          }}
          onDismiss={() => {
            const timeSpentMs = feedbackStartTimeRef.current ? Date.now() - feedbackStartTimeRef.current : undefined;
            // Track which screen user cancelled from (for scenario DataBag journey tracking)
            collectFeedbackCompletion({
              feedbackProvided: false,
              feedbackSubmitted: false,
              feedbackCancelled: true,
              feedbackSource: 'footer',
              currentScreen: location.pathname,
              timeSpentMs,
            });
            setShowFeedbackModal(false);
          }}
        />
      )}
    </>
  );
}